import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

interface NotificationDropdownProps {
  onClose: () => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const {
    notifications,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  const handleMarkAsRead = async (notificationId: string) => {
    await markAsRead(notificationId);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleDelete = async (notificationId: string) => {
    await deleteNotification(notificationId);
  };

  const handleNotificationClick = (notification: any) => {
    // Mark as read on click
    if (!notification.isRead) {
      markAsRead(notification.id);
    }

    // Navigate based on notification data
    const data = notification.data;
    if (data?.requestId) {
      navigate(`/requests/${data.requestId}`);
      onClose();
    } else if (data?.offerId) {
      navigate(`/offers`);
      onClose();
    } else {
      navigate('/notifications');
      onClose();
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'NEW_OFFER':
        return '💰';
      case 'OFFER_ACCEPTED':
        return '✅';
      case 'OFFER_REJECTED':
        return '❌';
      case 'REQUEST_EXPIRED':
      case 'OFFER_EXPIRED':
        return '⏰';
      case 'SELLER_APPROVED':
        return '🎉';
      case 'SELLER_REJECTED':
        return '😞';
      case 'SYSTEM':
        return 'ℹ️';
      default:
        return '🔔';
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'NEW_OFFER':
        return 'bg-primary-50 border-primary-200';
      case 'OFFER_ACCEPTED':
        return 'bg-accent-50 border-accent-200';
      case 'OFFER_REJECTED':
        return 'bg-danger-50 border-danger-200';
      case 'REQUEST_EXPIRED':
      case 'OFFER_EXPIRED':
        return 'bg-gold-50 border-gold-200';
      case 'SELLER_APPROVED':
        return 'bg-accent-50 border-accent-200';
      case 'SELLER_REJECTED':
        return 'bg-danger-50 border-danger-200';
      case 'SYSTEM':
        return 'bg-surface-50 border-surface-200';
      default:
        return 'bg-surface-50 border-surface-200';
    }
  };

  return (
    <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-surface-200 z-50 animate-scaleIn">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-surface-200">
        <h3 className="text-lg font-semibold text-surface-900">Notificaciones</h3>
        <div className="flex items-center space-x-2">
          {notifications.some(n => !n.isRead) && (
            <button
              onClick={handleMarkAllAsRead}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
            >
              Marcar todas como leídas
            </button>
          )}
          <button
            onClick={onClose}
            className="text-surface-400 hover:text-surface-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-surface-400 mb-2">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4 19h11a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-surface-500 text-sm">No tienes notificaciones</p>
          </div>
        ) : (
          <div className="divide-y divide-surface-100">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`p-4 hover:bg-surface-50 transition-colors duration-200 cursor-pointer ${
                  !notification.isRead ? 'bg-primary-50' : ''
                }`}
              >
                <div className="flex items-start space-x-3">
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                    getNotificationColor(notification.type)
                  }`}>
                    {getNotificationIcon(notification.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${
                          !notification.isRead ? 'text-surface-900' : 'text-surface-700'
                        }`}>
                          {notification.title}
                        </p>
                        <p className={`text-sm mt-1 ${
                          !notification.isRead ? 'text-surface-700' : 'text-surface-500'
                        }`}>
                          {notification.message}
                        </p>
                        <p className="text-xs text-surface-400 mt-2">
                          {formatDistanceToNow(new Date(notification.createdAt), {
                            addSuffix: true,
                            locale: es,
                          })}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-1 ml-2" onClick={(e) => e.stopPropagation()}>
                        {!notification.isRead && (
                          <button
                            onClick={() => handleMarkAsRead(notification.id)}
                            className="text-primary-600 hover:text-primary-700 text-xs font-medium"
                            title="Marcar como leída"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(notification.id)}
                          className="text-surface-400 hover:text-danger-600 text-xs"
                          title="Eliminar notificación"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Unread indicator */}
                    {!notification.isRead && (
                      <div className="absolute left-2 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-primary-600 rounded-full"></div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="px-4 py-3 border-t border-surface-200 bg-surface-50">
          <button
            onClick={() => { navigate('/notifications'); onClose(); }}
            className="w-full text-center text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Ver todas las notificaciones
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;